import { useEffect, useState } from 'react';

/**
 * useToggleDialog 是一個自訂 Hook，用於控制彈跳視窗（Dialog）的開關狀態。
 * 支援指定開啟目標與額外狀態（例如參數或附加設定）。
 *
 * @returns {Object} 一個包含以下屬性的物件：
 * - openTarget：目前被開啟的 Dialog 鍵值（如 'editUser'）
 * - handleOpenDialog(key: string, other?: Object)：開啟 Dialog 並設定額外狀態
 * - handleCloseDialog()：關閉 Dialog，將 target 重設為空字串
 * - ...other：來自 `handleOpenDialog` 所設的額外資料（如 id, type 等）
 *
 * @example
 * const { openTarget, handleOpenDialog, handleCloseDialog, userId } = useToggleDialog();
 *
 * <Button onClick={() => handleOpenDialog('editUser', { userId: 5 })}>編輯</Button>
 *
 * {openTarget === 'editUser' && (
 *   <Dialog onClose={handleCloseDialog}>
 *     <UserForm id={userId} />
 *   </Dialog>
 * )}
 */
const useToggleDialog = () => {
    const [openDialog, setOpenDialog] = useState({
        target: '',
    });
    const { target, ...other } = openDialog;

    /**
     * 開啟指定 Dialog，並可攜帶其他資料
     *
     * @param {string} key - Dialog 的名稱或識別鍵
     * @param {Object} [other] - 附加的狀態資料（如 id, type 等）
     */
    const handleOpenDialog = (key, other) => {
        setOpenDialog({ target: key, ...other });
    };

    /**
     * 關閉 Dialog，重設 target
     */
    const handleCloseDialog = () => {
        setOpenDialog({ target: '' });
    };

    useEffect(() => {
        return () => {
            setOpenDialog({ target: '' });
        };
        // eslint-disable-next-line
    }, []);

    return {
        openTarget: target,
        handleOpenDialog,
        handleCloseDialog,
        ...other,
    };
};

export default useToggleDialog;
